// Builds a SlotResolver that hydrates `$slot.X` references for preview
// surfaces (templates library cards, the email-builder editor). The same
// logic lives behind both consumers so the editor never shows a raw
// `$slot.copy.headline` while the library card shows "Headline" — they
// resolve identically off the same brand context, recent-media pool, and
// product pool.
//
// Server-side template fill (filler+remix) does its own resolution at
// insert/persist time. This helper is preview-only.

import {
  BLANK_PRODUCT_ID,
  extractSlots,
  stockImageForSlot,
  type BrandResolverContext,
  type EmailDocument,
  type PickedProduct,
  type SlotResolver,
} from '@nexmail/shared-util/email-builder';

export type PickRecentMedia = (
  slotName: string,
  used: Set<string>,
  offset?: number,
) => string | undefined;

export type PickProduct = (
  slotName: string,
  used: Set<string>,
) => string | undefined;

export type PickProductRich = (
  slotName: string,
  used: Set<string>,
) => PickedProduct | undefined;

// Mirrors pickTransparentProductMedia from shared-util's section-resolver,
// minus the productId (preview doesn't need to stamp variants on nodes).
export type PickTransparentMedia = (
  slotName: string,
  used: Set<string>,
  offset?: number,
) => string | undefined;

// "heroHeadline" → "Hero Headline". Keeps copy slots readable when no
// concrete fill exists yet.
function humanizeSlotName(name: string): string {
  return name
    .replace(/[-_]/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

const SLOT_REF_RE = /^\$slot\.([a-zA-Z][a-zA-Z0-9_]*)$/;

// Walk the document for image nodes flagged `productCutout: true` and
// collect the slot names their `src` props bind to. The resolver uses this
// set to route those slots through pickTransparentMedia first so a brand's
// transparent PNGs win over generic recent uploads — without this, the
// preview just rotates the recent-media pool and a lifestyle photo lands
// where a cutout was meant to be. Mirrors stampProductCutoutIds from
// shared-util's section-resolver, but preview-only and without productId
// stamping.
function collectCutoutSlotNames(node: unknown, out: Set<string>): void {
  if (node == null) return;
  if (Array.isArray(node)) {
    for (const item of node) collectCutoutSlotNames(item, out);
    return;
  }
  if (typeof node !== 'object') return;
  const obj = node as Record<string, unknown>;
  if (obj.type === 'image' && obj.props && typeof obj.props === 'object') {
    const props = obj.props as Record<string, unknown>;
    if (props.productCutout === true && typeof props.src === 'string') {
      const m = SLOT_REF_RE.exec(props.src);
      if (m) out.add(m[1]);
    }
  }
  for (const k of Object.keys(obj)) {
    const v = obj[k];
    if (v && typeof v === 'object') collectCutoutSlotNames(v, out);
  }
}

export function buildPreviewSlotResolver(args: {
  document: EmailDocument;
  brand: BrandResolverContext | undefined;
  pickRecentMedia: PickRecentMedia;
  pickProduct: PickProduct;
  // Optional. When provided, image slots that feed `productCutout: true`
  // image nodes try this picker first and only fall through to
  // pickRecentMedia if it returns undefined (no transparent assets in pool).
  pickTransparentMedia?: PickTransparentMedia;
  // Optional. When provided, dotted slot refs (`$slot.productN.cutoutImage` /
  // `.name` / `.price` / `.productUrl`) dispatch fields off a single picked
  // product. Cached per base slot so all four refs share one PickedProduct.
  pickProductRich?: PickProductRich;
  mediaOffset?: number;
}): SlotResolver {
  const { document, brand, pickRecentMedia, pickProduct, pickTransparentMedia, pickProductRich, mediaOffset } = args;
  const slots = extractSlots(document);
  const cutoutSlots = new Set<string>();
  collectCutoutSlotNames(document, cutoutSlots);
  const usedMedia = new Set<string>();
  const usedProducts = new Set<string>();
  const productBySlot = new Map<string, PickedProduct>();

  return (slotName: string): string | undefined => {
    // Dotted ref (`<base>.<field>`) — only valid against a kind=product slot.
    // Cache the picked product so every dotted ref off the same base lands on
    // the same product.
    const dotIdx = slotName.indexOf('.');
    if (dotIdx > 0) {
      const baseName = slotName.slice(0, dotIdx);
      const field = slotName.slice(dotIdx + 1);
      const baseSlot = slots.find((s) => s.name === baseName);
      if (!baseSlot || baseSlot.expects !== 'product-id') return undefined;
      let picked = productBySlot.get(baseName);
      if (!picked && pickProductRich) {
        const fresh = pickProductRich(baseName, usedProducts);
        if (fresh) {
          usedProducts.add(fresh.variantId);
          productBySlot.set(baseName, fresh);
          picked = fresh;
        }
      }
      if (!picked) return undefined;
      switch (field) {
        case 'id':
        case 'variantId':
          return picked.variantId;
        case 'productId':
          return picked.productId;
        case 'name':
        case 'title':
          return picked.name;
        case 'price':
          return picked.price ?? '';
        case 'compareAtPrice':
          return picked.compareAtPrice ?? '';
        case 'cutoutImage':
          return picked.transparentImageUrl ?? picked.fallbackImageUrl;
        case 'image':
          return picked.fallbackImageUrl;
        case 'url':
        case 'productUrl':
          return picked.productUrl;
        default:
          return undefined;
      }
    }

    const slot = slots.find((s) => s.name === slotName);
    if (!slot) return undefined;
    switch (slot.expects) {
      case 'image-url': {
        if (cutoutSlots.has(slotName) && pickTransparentMedia) {
          const transparent = pickTransparentMedia(slotName, usedMedia, mediaOffset);
          if (transparent) {
            usedMedia.add(transparent);
            return transparent;
          }
        }
        const url = pickRecentMedia(slotName, usedMedia, mediaOffset);
        if (url) {
          usedMedia.add(url);
          return url;
        }
        return stockImageForSlot(slotName);
      }
      case 'product-id': {
        // Bare ref reuses any product the rich picker already cached for this
        // slot (e.g. a section that mixes `$slot.productN` for a product-feed
        // source with `$slot.productN.name` elsewhere).
        const cached = productBySlot.get(slotName);
        if (cached) return cached.variantId;
        const id = pickProduct(slotName, usedProducts);
        if (id) {
          usedProducts.add(id);
          return id;
        }
        return BLANK_PRODUCT_ID;
      }
      case 'href':
        return brand?.website ?? '#';
      case 'copy':
        return humanizeSlotName(slotName);
      default:
        return undefined;
    }
  };
}
